import { useState, useEffect } from 'react';

export const useLocalStorageChange = <T>(key: string): [T | null, (value: T | null) => void] => {
  const [value, setValue] = useState<T | null>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return null;
    }
  });

  // Функция для обновления localStorage и триггера событий
  const updateStorage = (newValue: T | null) => {
    try {
      const stringValue = JSON.stringify(newValue);
      localStorage.setItem(key, stringValue);
      
      // 1. Стандартное событие (для других вкладок)
      window.dispatchEvent(new StorageEvent('storage', { 
        key, 
        newValue: stringValue,
        oldValue: localStorage.getItem(key),
        storageArea: localStorage,
      }));
      
      // 2. Кастомное событие (для текущей вкладки)
      window.dispatchEvent(new CustomEvent('localStorageChange', { 
        detail: { key, newValue: stringValue } 
      }));
      
      setValue(newValue);
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  useEffect(() => {
    const handleChange = (event: StorageEvent | CustomEvent) => {
      let eventKey: string | null;
      let eventValue: string | null;

      if (event instanceof StorageEvent) {
        eventKey = event.key;
        eventValue = event.newValue;
      } else {
        // Обработка кастомного события
        eventKey = event.detail.key;
        eventValue = event.detail.newValue;
      }

      if (eventKey === key) {
        try {
          setValue(eventValue ? JSON.parse(eventValue) : null);
        } catch (error) {
          console.error(`Error parsing localStorage value:`, error);
        }
      }
    };

    window.addEventListener('storage', handleChange);
    window.addEventListener('localStorageChange', handleChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('localStorageChange', handleChange as EventListener);
    };
  }, [key]);

  return [value, updateStorage];
};